import { useMutation } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';
import { useCoverageUploadPolling } from '@/features/codeCoverage/hooks/useCoverageUploadPolling';
import { emitCoverageRefresh } from '@/features/codeCoverage/state/coverageRefreshSignal';
import { useOrganisationFilter } from '@/features/organisations/providers/organisationHooks';
import { useProjectFilter } from '@/features/projects/hooks/projectHooks';
import { useProjectsQuery } from '@/features/projects/hooks/projects';
import { commitUpload, createUpload, uploadFile } from '@/features/uploads/data';
import { useNotifications } from '@/providers/notificationHooks';
import { getErrorCopy } from '@/shared/copy/errors';
import { getToastMessage } from '@/shared/copy/toasts';
import { getErrorMessage } from '@/shared/data/errors';
import { useInvalidateQueries } from '@/shared/hooks';
import { buildCoverageUploadInvalidations } from '@/shared/hooks/uploadInvalidations';
import { markUploadToastIntent } from '@/shared/notifications/uploadToastIntent';
import type { ProjectDto } from '@flowqa/shared';
import type { Dispatch, SetStateAction } from 'react';

const isSupportedCoverageFile = (file: File): boolean => {
  const normalizedName = file.name.trim().toLowerCase();
  return (
    normalizedName.endsWith('.json') ||
    normalizedName.endsWith('.lcov') ||
    normalizedName.endsWith('.info') ||
    normalizedName.endsWith('.txt')
  );
};

type UseCoverageUploadFlowResult = {
  open: boolean;
  uploadProjectId: string;
  uploadFiles: File[];
  selectedFiles: Set<string>;
  uploadError: string | null;
  projectOptions: ProjectDto[];
  isUploading: boolean;
  disableUpload: boolean;
  openUploadDialog: () => void;
  closeUploadDialog: () => void;
  setUploadProjectId: (value: string) => void;
  setUploadFiles: Dispatch<SetStateAction<File[]>>;
  setSelectedFiles: Dispatch<SetStateAction<Set<string>>>;
  setUploadError: (value: string | null) => void;
  handleUpload: () => void;
};

export const useCoverageUploadFlow = (): UseCoverageUploadFlowResult => {
  const { selectedOrganisationId } = useOrganisationFilter();
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const invalidateQueries = useInvalidateQueries();
  const projectsQuery = useProjectsQuery({
    enabled: true,
    organisationId: selectedOrganisationId ?? null,
  });
  const [open, setOpen] = useState(false);
  const [uploadProjectId, setUploadProjectId] = useState(selectedProjectId ?? '');
  const [uploadFiles, setUploadFiles] = useState<File[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<Set<string>>(new Set());
  const [uploadError, setUploadError] = useState<string | null>(null);
  const resolvedProjectId = selectedProjectId ?? uploadProjectId;

  const projectOptions = useMemo(
    () => projectsQuery.data?.items ?? [],
    [projectsQuery.data?.items],
  );
  const invalidateCoverageData = useCallback(async (): Promise<void> => {
    await invalidateQueries(
      buildCoverageUploadInvalidations({
        selectedOrganisationId: selectedOrganisationId ?? null,
        selectedProjectId: selectedProjectId ?? null,
        uploadProjectId: resolvedProjectId,
      }),
    );
  }, [invalidateQueries, resolvedProjectId, selectedOrganisationId, selectedProjectId]);
  const pollCoverageUpload = useCoverageUploadPolling({
    selectedOrganisationId: selectedOrganisationId ?? null,
    invalidateCoverageData,
    notify,
  });
  const selectedUploadFiles = useMemo(
    () =>
      uploadFiles.filter((file) => selectedFiles.has(file.name) && isSupportedCoverageFile(file)),
    [selectedFiles, uploadFiles],
  );

  const uploadMutation = useMutation({
    mutationFn: async (files: File[]) => {
      if (!resolvedProjectId) {
        throw new Error(getErrorCopy('upload.selectProject'));
      }
      const failures: string[] = [];
      const committedUploadIds: string[] = [];
      for (const file of files) {
        try {
          const created = await createUpload(
            resolvedProjectId,
            {
              purpose: 'coverage',
              source: 'manual',
              fileName: file.name,
              contentType: file.type || 'application/octet-stream',
              sourceKey: 'manual-upload',
            },
            selectedOrganisationId ?? null,
          );
          markUploadToastIntent(created.uploadId);
          await uploadFile(created.uploadId, file, selectedOrganisationId ?? null);
          await commitUpload(created.uploadId, selectedOrganisationId ?? null);
          committedUploadIds.push(created.uploadId);
        } catch (error) {
          failures.push(`${file.name}: ${getErrorMessage(error)}`);
        }
      }
      return { committedUploadIds, failures };
    },
    onSuccess: async (result) => {
      setOpen(false);
      setUploadFiles([]);
      setSelectedFiles(new Set());
      setUploadError(null);
      await invalidateCoverageData();
      emitCoverageRefresh();
      result.committedUploadIds.forEach((uploadId) => {
        pollCoverageUpload(uploadId);
      });
      result.failures.forEach((failure) => {
        notify({ message: failure, tone: 'red' });
      });
    },
    onError: (error) => {
      setUploadError(getErrorMessage(error));
    },
  });

  const openUploadDialog = useCallback(() => {
    setUploadProjectId(selectedProjectId ?? '');
    setOpen(true);
  }, [selectedProjectId]);

  const closeUploadDialog = useCallback(() => {
    if (uploadMutation.isPending) {
      return;
    }
    setOpen(false);
  }, [uploadMutation.isPending]);

  const handleUpload = useCallback(() => {
    if (!resolvedProjectId) {
      setUploadError(getErrorCopy('upload.selectProject'));
      return;
    }
    if (selectedUploadFiles.length === 0) {
      setUploadError(getErrorCopy('upload.selectSupportedCoverageFile'));
      return;
    }
    setUploadError(null);
    const fileLabel = selectedUploadFiles.length === 1 ? 'coverage file' : 'coverage files';
    notify({
      message: getToastMessage('upload.filesUploading', {
        count: selectedUploadFiles.length,
        fileLabel,
      }),
      tone: 'blue',
    });
    uploadMutation.mutate(selectedUploadFiles);
  }, [notify, resolvedProjectId, selectedUploadFiles, uploadMutation]);

  return {
    open,
    uploadProjectId,
    uploadFiles,
    selectedFiles,
    uploadError,
    projectOptions,
    isUploading: uploadMutation.isPending,
    disableUpload: selectedUploadFiles.length === 0 || !resolvedProjectId,
    openUploadDialog,
    closeUploadDialog,
    setUploadProjectId,
    setUploadFiles,
    setSelectedFiles,
    setUploadError,
    handleUpload,
  };
};
